import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import WorkRecordApi from "../../api/workRecordApi";
import WorkRecordListItem from "../../components/workrecord/WorkRecordListItem";
import { useJWT } from "../../contexts/JWTContext";
import { BarChart3, Calendar } from "lucide-react";

function ListWorkRecordPage() {
  const navigate = useNavigate();
  const { userId, isLoading } = useJWT();

  const [searchParams] = useSearchParams();

  const [startDate, setStartDate] = useState(
    searchParams.get("startDate") || ""
  );
  const [endDate, setEndDate] = useState(searchParams.get("endDate") || "");
  const [type, setType] = useState(searchParams.get("type") || "");

  const [list, setList] = useState([]);

  // 로그인 체크
  useEffect(() => {
    // 아직 JWT 로딩 중이면 아무 것도 하지 않음
    if (isLoading || !userId) return;

    // 로딩 끝났는데 userId 없으면 → 로그인 페이지
    if (!userId) {
      navigate("/");
    }
  }, [isLoading, userId, navigate]);

  // 운동기록 리스트 API 호출
  useEffect(() => {
    if (!userId) return;

    WorkRecordApi.listByUser(userId, startDate, endDate, type)
      .then((res) => {
        setList(res.data);
      })
      .catch((err) => console.error("listByUser API error:", err));
  }, [userId, startDate, endDate, type]);

  return (
    <div className="h-screen overflow-hidden bg-white flex flex-col">
      {/* HEADER - 상단 고정 */}
      <header className="bg-white px-5 py-4 pt-6 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div className="w-10"></div>
          <h1 className="text-2xl font-bold text-gray-800">운동기록</h1>
          <button
            onClick={() =>
              navigate(
                `/workrecord/stats/${userId}?startDate=${startDate}&endDate=${endDate}&type=${type}`
              )
            }
            className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-gray-50 transition-colors"
            style={{ color: "rgb(0, 224, 198)" }}
          >
            <BarChart3 className="w-6 h-6" />
          </button>
        </div>

        {/* 필터 영역 */}
        <div className="mb-2">
          {/* 날짜 범위 */}
          <div className="mb-3">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-gray-600" />
              <label className="text-sm font-semibold text-gray-700">
                기간 선택
              </label>
            </div>

            <div className="flex items-center gap-2">
              {/* 시작일 */}
              <div className="flex-1 min-w-0 relative">
                {/* 보이는 UI (placeholder 보이게) */}
                <div
                  className="
                  w-full h-11 px-3
                  flex items-center justify-between
                  text-sm
                  border-2 border-gray-200 rounded-xl
                 bg-white
                 text-gray-800"
                >
                  {startDate ? (
                    <span>{startDate}</span>
                  ) : (
                    <span className="text-gray-800">연도-월-일</span>
                  )}

                  <Calendar className="w-4 h-4 text-gray-800 shrink-0 ml-2" />
                </div>

                {/* 실제 date input (투명 오버레이: 탭하면 iOS 피커 열림) */}
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="
                  absolute inset-0 w-full h-full
                  opacity-0
                  cursor-pointer"
                />
              </div>

              <span className="text-gray-800 font-medium shrink-0">-</span>

              {/* 종료일 */}
              <div className="flex-1 min-w-0 relative">
                <div
                  className="
                  w-full h-11 px-3
                  flex items-center justify-between
                  text-sm
                  border-2 border-gray-200 rounded-xl
                  bg-white
                  text-gray-800"
                >
                  {endDate ? (
                    <span>{endDate}</span>
                  ) : (
                    <span className="text-gray-800">연도-월-일</span>
                  )}

                  <Calendar className="w-4 h-4 text-gray-800 shrink-0 ml-2" />
                </div>

                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="
                  absolute inset-0 w-full h-full
                  opacity-0
                  cursor-pointer"
                />
              </div>
            </div>
          </div>

          {/* 운동 종류 */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              운동 종류
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => setType("")}
                className={`flex-1 py-3 px-4 rounded-xl text-sm font-semibold transition-all ${
                  type === ""
                    ? "text-white shadow-md"
                    : "bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200"
                }`}
                style={
                  type === "" ? { backgroundColor: "rgb(0, 224, 198)" } : {}
                }
              >
                전체
              </button>
              <button
                onClick={() => setType("러닝")}
                className={`flex-1 py-3 px-4 rounded-xl text-sm font-semibold transition-all ${
                  type === "러닝"
                    ? "text-white shadow-md"
                    : "bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200"
                }`}
                style={
                  type === "러닝" ? { backgroundColor: "rgb(0, 224, 198)" } : {}
                }
              >
                러닝
              </button>
              <button
                onClick={() => setType("산책")}
                className={`flex-1 py-3 px-4 rounded-xl text-sm font-semibold transition-all ${
                  type === "산책"
                    ? "text-white shadow-md"
                    : "bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200"
                }`}
                style={
                  type === "산책" ? { backgroundColor: "rgb(0, 224, 198)" } : {}
                }
              >
                산책
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* 운동기록 리스트 - 스크롤 영역 */}
      <main className="flex-1 overflow-y-auto px-5 py-4 bg-gradient-to-b from-blue-50 to-white">
        <div className="space-y-3 pb-24">
          {list.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-6xl mb-4">🏃</div>
              <p className="text-gray-600 font-semibold mb-2">
                운동 기록이 없습니다
              </p>
              <p className="text-sm text-gray-400">첫 운동을 시작해보세요!</p>
            </div>
          ) : (
            list.map((item) => (
              <WorkRecordListItem key={item.workRecordId} item={item} />
            ))
          )}
        </div>
      </main>
    </div>
  );
}

export default ListWorkRecordPage;
